import { Outlet } from 'react-router-dom'

import { PermissionsRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { Employee, useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import CollectionsBootstrap from 'modules/permissions/lib/SettingsCollectionsBootstrap'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'

export interface PermissionsContainerContext {
  loading: boolean
}

const userIsAbleToSeeSettings = (
  employee: Employee,
  canGroupCreate: boolean,
  canGroupEdit: boolean,
  canAnyPermissions: boolean
) => {
  if (employee && !employee.hasRegularAccess) return false

  return canGroupCreate || canGroupEdit || canAnyPermissions
}

const PermissionsRootPage = () => {
  const { employee } = useCurrentSession()
  const { data, loading: loadingPageData } = useQuery(PermissionsRootPageDocument)

  const canCompanyManagePermissions = data?.apiCore._policies.companyManagePermissions.can ?? false
  const canGroupCreate = data?.permissions._policies.groupCreate.can ?? false
  const canGroupEdit = data?.permissions._policies.groupEdit.can ?? false
  const canAnyPermissions = data?.permissions._policies.permissionsRead.can ?? false

  const canManagePermissions =
    userIsAbleToSeeSettings(employee, canGroupCreate, canGroupEdit, canAnyPermissions) ||
    canCompanyManagePermissions

  const { loading: loadingPermissionGroups } = useAsyncData(() => {
    return permissionGroups.fetch()
  }, [])

  const loading = loadingPermissionGroups || loadingPageData

  return (
    <CollectionsBootstrap>
      <AuthorizeUser authorizeFn={() => canManagePermissions}>
        <Outlet context={{ loading }} />
      </AuthorizeUser>
    </CollectionsBootstrap>
  )
}

export default PermissionsRootPage
